import { create } from "zustand";
import ICoreBundle from "../interfaces/ICoreBundle";
import ICoreApartmentBundles, { ICoreApartmentRoom } from "../interfaces/ICoreApartmentBundles";
import { normalizeGuid } from "../utils/guidUtils";

/** An element of a bundle, as listed in the bundle inspector's panel. */
export interface BundleElement {
    globalId: string;       // Normalised with normalizeGuid()
    name: string | null;
    roomNames: string[];    // The rooms CoreAPI lists it in, usually one
    roomElementIds: number[];   // CoreAPI's ids for it, one per room it is listed in
    roomIds: number[];          // The rooms it is listed in
}

/** The apartment's bundles indexed both ways, keyed by elements' normalised globalIds. */
export interface ElementBundleIndex {
    bundlesByElement: Map<string, ICoreBundle[]>;
    elementsByBundle: Map<number, Map<string, BundleElement>>;
    bundlesById: Map<number, ICoreBundle>;
    rooms: ICoreApartmentRoom[];    // Every room of the apartment
}

// Memberships of unknown bundles (hidden from the user, or missing from the response) are dropped
export const buildElementBundleIndex = (data: ICoreApartmentBundles): ElementBundleIndex => {
    const bundlesById = new Map(data.bundles.map(b => [b.id, b]));
    const bundlesByElement = new Map<string, ICoreBundle[]>();
    const elementsByBundle = new Map<number, Map<string, BundleElement>>();

    for (const { globalId, roomElementId, bundleId, name, roomId, roomName } of data.elements) {
        const bundle = bundlesById.get(bundleId);
        if (!bundle) continue;

        const bundles = bundlesByElement.get(globalId) ?? [];
        if (!bundles.includes(bundle)) bundles.push(bundle); // An element is listed once per room it is in
        bundlesByElement.set(globalId, bundles);

        const elements = elementsByBundle.get(bundleId) ?? new Map<string, BundleElement>();
        const element = elements.get(globalId) ?? { globalId, name, roomNames: [], roomElementIds: [], roomIds: [] };
        if (roomName && !element.roomNames.includes(roomName)) element.roomNames.push(roomName);
        if (!element.roomElementIds.includes(roomElementId)) element.roomElementIds.push(roomElementId);
        if (!element.roomIds.includes(roomId)) element.roomIds.push(roomId);
        elements.set(globalId, element);
        elementsByBundle.set(bundleId, elements);
    }

    return { bundlesByElement, elementsByBundle, bundlesById, rooms: data.rooms };
};

// Null when the bundle isn't in the data, or is hidden from the user
export const getBundleById = (index: ElementBundleIndex, bundleId: number): ICoreBundle | null =>
    index.bundlesById.get(bundleId) ?? null;

// The bundles an element belongs to. Should be at most one; more is a data error the inspector warns about.
export const getBundlesForElement = (index: ElementBundleIndex, globalId: string): ICoreBundle[] => {
    const key = normalizeGuid(globalId);
    return key ? index.bundlesByElement.get(key) ?? [] : [];
};

// The normalised globalIds of every element in the given bundles
export const getElementsInBundles = (index: ElementBundleIndex, bundles: ICoreBundle[]): Set<string> => {
    const result = new Set<string>();
    bundles.forEach(b => index.elementsByBundle.get(b.id)?.forEach((_, e) => result.add(e)));
    return result;
};

// A bundle's elements in CoreAPI's order
export const getBundleElements = (index: ElementBundleIndex, bundle: ICoreBundle): BundleElement[] =>
    [...index.elementsByBundle.get(bundle.id)?.values() ?? []];

/**
 * Bundles from CoreAPI for the bundle inspector, loaded on first use and kept for the session.
 * The viewer shows one apartment per page load, so there is a single cache.
 */
interface ElementBundlesState {
    index: ElementBundleIndex | null;
    pending: Promise<ElementBundleIndex | null> | null;

    // Null when loading failed. A failure isn't cached, so the next call tries again.
    loadIndex: (load: () => Promise<ICoreApartmentBundles | null>) => Promise<ElementBundleIndex | null>;
    // Drops the loaded bundles and loads them again, e.g. after they were changed in CoreAPI
    reload: (load: () => Promise<ICoreApartmentBundles | null>) => Promise<ElementBundleIndex | null>;
}

export const useElementBundlesStore = create<ElementBundlesState>()((set, get) => ({
    index: null,
    pending: null,

    loadIndex: (load) => {
        const { index, pending } = get();
        if (index) return Promise.resolve(index);
        if (pending) return pending;

        const request = load()
            .then(data => data ? buildElementBundleIndex(data) : null)
            .catch(() => null)
            .then(result => {
                // A reload meanwhile replaced this request, and its newer data wins
                if (get().pending === request) set({ index: result, pending: null });
                return result;
            });

        set({ pending: request });
        return request;
    },

    reload: (load) => {
        set({ index: null, pending: null });
        return get().loadIndex(load);
    },
}));
